'use client'

import classnames from 'classnames'
import styles from './ButtonDropdown.module.scss'
import Button from '@/components/Button/Button'
import Link from '@/components/Link/Link'
import { useRef, useState } from 'react'

type OrderDropdownCustomButtonItems = {
  label: string
  callback: () => void
}

type ButtonDropdownProps = {
  className?: string
  serviceLinks?: SanityLocationServiceLinkType[]
  customButtonItems?: OrderDropdownCustomButtonItems[]
  dropdownPosition?: 'bottomLeft' | 'bottomRight' | 'topRight' | 'topLeft'
  label?: string
  primary?: boolean
  isReserve?: boolean
  onLinkClick?: () => void
  slug?: string
  isSoftOpening?: boolean
  linkNested?: SanityLink[]
}

const ButtonDropdown = ({
  className,
  dropdownPosition = 'bottomLeft',
  label,
  primary,
  linkNested,
  onLinkClick,
}: ButtonDropdownProps) => {
  const [isFocused, setIsFocused] = useState(false)
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasLinkClicked = useRef(false)

  const handleClick = () => {
    if (hasLinkClicked.current) return
    hasLinkClicked.current = true
    setTimeout(() => {
      hasLinkClicked.current = false
    }, 50)
    setIsFocused(prev => !prev)
  }

  const handleLinkClick = () => {
    if (onLinkClick) onLinkClick()
    setIsFocused(false)
  }

  const handleLinkFocus = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current)
    }
    setIsFocused(true)
  }

  return (
    <div
      className={classnames(styles.ButtonDropdown, className, { [styles.isFocused]: isFocused })}
      data-dropdown-position={dropdownPosition}
    >
      <Button
        onClick={handleClick}
        onBlur={() => {
          if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current)
          }
          blurTimeoutRef.current = setTimeout(() => {
            setIsFocused(false)
          }, 300)
        }}
        primary={primary}
        secondary={!primary}
        icon={'caretDown'}
        label={label}
        className={styles.button}
      />
      {
        <div
          className={styles.dropdown}
          data-lenis-prevent
        >
          <div className={styles.dropdown__inner}>
            {linkNested?.map((link, index) => (
              <Link
                key={index}
                className={styles.dropdownLink}
                link={link}
                onClick={handleLinkClick}
                onFocus={handleLinkFocus}
              />
            ))}
          </div>
        </div>
      }
    </div>
  )
}

ButtonDropdown.displayName = 'OrderDropdown'

export default ButtonDropdown
